import type {
  ConditionLifecycleIncidentSummary,
  ConditionLifecycleScanSummary,
  ConditionLifecycleSummary,
} from './siteConditionState';

export const CORE_CONDITION_FAMILIES = [
  'PERSON_FORKLIFT_PROXIMITY',
  'RESTRICTED_AREA_ENTRY',
  'PPE_NON_COMPLIANCE',
] as const;

export type CoreConditionFamily = (typeof CORE_CONDITION_FAMILIES)[number];

export type ControlAuthority = 'epic402' | 'legacy' | 'none';

export type PostControlPatternStatus =
  | 'no_control'
  | 'none_in_measured_period'
  | 'continued';

export type ConditionLifecycleControlView = {
  authority: ControlAuthority;
  controlRevisionId: string | null;
  sinceStartedAt: string | null;
  label: string;
  legacyNote: string | null;
};

export type PatternAlertReasonView = {
  triggerKind: 'TEMPORAL_PATTERN';
  minEvents: number | null;
  windowSeconds: number | null;
  observedCount: number | null;
  triggeredAt: string | null;
  summary: string;
};

export type PostControlPatternView = {
  status: PostControlPatternStatus;
  count: number;
  latestIncidentId: string | null;
  latestOccurredAt: string | null;
  coverageState: string | null;
  finality: string | null;
  summary: string;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

export function canonicalConditionFamily(family: string | null | undefined): string {
  const normalized = text(family).toUpperCase().replace(/-/g, '_');
  if (
    normalized === 'PERSON_FORKLIFT_PROXIMITY'
    || normalized === 'FORKLIFT_PROXIMITY'
    || normalized === 'PEDESTRIAN_NEAR_MOVING_FORKLIFT'
    || normalized === 'NEAR_MISS'
  ) {
    return 'PERSON_FORKLIFT_PROXIMITY';
  }
  if (
    normalized === 'RESTRICTED_AREA_ENTRY'
    || normalized === 'RESTRICTED_ZONE'
    || normalized === 'ZONE_INTRUSION'
    || normalized === 'ZONE_VIOLATION'
  ) {
    return 'RESTRICTED_AREA_ENTRY';
  }
  if (
    normalized === 'PPE_NON_COMPLIANCE'
    || normalized === 'PPE_COMPLIANCE'
    || normalized === 'PPE_VIOLATION'
  ) {
    return 'PPE_NON_COMPLIANCE';
  }
  return text(family) || normalized;
}

export function isCoreConditionFamily(family: string | null | undefined): boolean {
  return CORE_CONDITION_FAMILIES.includes(
    canonicalConditionFamily(family) as CoreConditionFamily,
  );
}

export function presentCustomerPatternWindowCopy(
  minEvents: number | null | undefined,
  windowSeconds: number | null | undefined,
): string | null {
  if (minEvents == null || windowSeconds == null) return null;
  if (!Number.isFinite(minEvents) || !Number.isFinite(windowSeconds)) return null;
  if (windowSeconds % 60 === 0) {
    const minutes = windowSeconds / 60;
    return `${minEvents} qualifying occurrences within ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${minEvents} qualifying occurrences within ${windowSeconds} seconds`;
}

export function presentCustomerPatternReasonSummary(
  reason: PatternAlertReasonView | null | undefined,
): string | null {
  if (!reason) return null;
  const windowCopy = presentCustomerPatternWindowCopy(reason.minEvents, reason.windowSeconds);
  const observed = reason.observedCount == null ? 'observed count unavailable' : `${reason.observedCount} observed`;
  if (windowCopy) {
    return `Pattern Alert fired because ${windowCopy} was met (${observed}). Recurrence on Overview is not this alarm.`;
  }
  return reason.summary;
}

export function presentCustomerPostControlPatternStatus(
  status: string | null | undefined,
): string | null {
  if (status === 'none_in_measured_period') return 'No Pattern Alerts in the measured period';
  if (status === 'continued') return 'Pattern Alerts continued in the measured period';
  if (status === 'no_control') return 'No Control is linked';
  return null;
}

export function presentCustomerResultDirection(direction: string | null | undefined): string | null {
  const key = text(direction).toLowerCase();
  if (key === 'decreased') return 'Fewer qualifying occurrences observed';
  if (key === 'increased') return 'More qualifying occurrences observed';
  if (key === 'no_change' || key === 'unchanged') return 'No change in qualifying occurrences observed';
  return null;
}

export function parsePatternRule(patternRule: string | null | undefined): {
  minEvents: number | null;
  windowSeconds: number | null;
} {
  const raw = text(patternRule);
  const match = raw.match(/(\d+)\s+in\s+(\d+|—)s/i);
  if (!match) return { minEvents: null, windowSeconds: null };
  const minEvents = Number(match[1]);
  const windowSeconds = match[2] === '—' ? null : Number(match[2]);
  return {
    minEvents: Number.isFinite(minEvents) ? minEvents : null,
    windowSeconds: windowSeconds != null && Number.isFinite(windowSeconds) ? windowSeconds : null,
  };
}

export function presentPatternAlertReason(
  incident: ConditionLifecycleIncidentSummary | null | undefined,
): PatternAlertReasonView | null {
  if (!incident || incident.triggerKind !== 'TEMPORAL_PATTERN') return null;
  const parsed = parsePatternRule(incident.patternRule);
  const minEvents = incident.patternMinEvents ?? parsed.minEvents;
  const windowSeconds = incident.patternWindowSeconds ?? parsed.windowSeconds;
  const observedCount = incident.observedCount;
  const nInT = minEvents != null && windowSeconds != null
    ? `${minEvents} events in ${windowSeconds} seconds`
    : (incident.patternRule || 'N-in-T threshold');
  const observed = observedCount == null ? 'observed count unavailable' : `${observedCount} observed`;
  return {
    triggerKind: 'TEMPORAL_PATTERN',
    minEvents,
    windowSeconds,
    observedCount,
    triggeredAt: incident.occurredAt,
    summary: `Pattern Alert fired because ${nInT} was met (${observed}). Recurrence on Overview is not this alarm.`,
  };
}

export function resolveCanonicalControl(args: {
  lifecycle?: ConditionLifecycleSummary | null;
  legacyControlId?: string | null;
  legacyControlTitle?: string | null;
}): ConditionLifecycleControlView {
  const lifecycle = args.lifecycle || null;
  if (lifecycle?.controlRevisionId) {
    return {
      authority: 'epic402',
      controlRevisionId: lifecycle.controlRevisionId,
      sinceStartedAt: lifecycle.sinceStartedAt,
      label: 'Control recorded',
      legacyNote: null,
    };
  }
  const legacyId = text(args.legacyControlId);
  const legacyTitle = text(args.legacyControlTitle);
  if (legacyId || legacyTitle) {
    return {
      authority: 'legacy',
      controlRevisionId: null,
      sinceStartedAt: null,
      label: legacyTitle || 'Legacy Control record',
      legacyNote: 'Legacy Control record — not linked through the condition lifecycle.',
    };
  }
  return {
    authority: 'none',
    controlRevisionId: null,
    sinceStartedAt: null,
    label: 'No Control recorded',
    legacyNote: null,
  };
}

export function presentPostControlPattern(args: {
  sinceStartedAt?: string | null;
  patternIncidents?: Array<Pick<ConditionLifecycleIncidentSummary, 'incidentId' | 'occurredAt' | 'triggerKind'>>;
  latestPattern?: ConditionLifecycleIncidentSummary | null;
  scan?: ConditionLifecycleScanSummary | null;
  backend?: {
    status?: string | null;
    count?: number | null;
    latestIncidentId?: string | null;
    latestOccurredAt?: string | null;
  } | null;
}): PostControlPatternView {
  const coverageState = args.scan?.coverageState || null;
  const finality = args.scan?.finality || null;
  const backend = args.backend;
  if (backend?.status === 'no_control' || backend?.status === 'none_in_measured_period' || backend?.status === 'continued') {
    const status = backend.status as PostControlPatternStatus;
    const count = Number(backend.count || 0);
    return {
      status,
      count,
      latestIncidentId: backend.latestIncidentId || null,
      latestOccurredAt: backend.latestOccurredAt || null,
      coverageState,
      finality,
      summary: postControlSummary(status, count, coverageState, finality),
    };
  }
  const since = text(args.sinceStartedAt);
  if (!since) {
    return {
      status: 'no_control',
      count: 0,
      latestIncidentId: null,
      latestOccurredAt: null,
      coverageState,
      finality,
      summary: postControlSummary('no_control', 0, coverageState, finality),
    };
  }
  const sinceMs = Date.parse(since);
  const incidents = (args.patternIncidents || []).filter((item) => item.triggerKind === 'TEMPORAL_PATTERN');
  const after = incidents.filter((item) => {
    const occurred = Date.parse(String(item.occurredAt || ''));
    return Number.isFinite(sinceMs) && Number.isFinite(occurred) && occurred > sinceMs;
  });
  const latest = args.latestPattern;
  if (latest?.occurredAt && Number.isFinite(sinceMs)) {
    const latestMs = Date.parse(latest.occurredAt);
    if (Number.isFinite(latestMs) && latestMs > sinceMs && !after.some((item) => item.incidentId === latest.incidentId)) {
      after.push(latest);
    }
  }
  after.sort((left, right) => Date.parse(String(right.occurredAt || '')) - Date.parse(String(left.occurredAt || '')));
  const status: PostControlPatternStatus = after.length ? 'continued' : 'none_in_measured_period';
  return {
    status,
    count: after.length,
    latestIncidentId: after[0]?.incidentId || null,
    latestOccurredAt: after[0]?.occurredAt || null,
    coverageState,
    finality,
    summary: postControlSummary(status, after.length, coverageState, finality),
  };
}

function postControlSummary(
  status: PostControlPatternStatus,
  count: number,
  coverageState: string | null,
  finality: string | null,
): string {
  const coverage = coverageState ? `Coverage ${coverageState}` : 'Coverage unavailable';
  const finalityLabel = finality || 'finality unavailable';
  if (status === 'no_control') {
    return `No Control implementation time is linked, so post-Control Pattern Alert status cannot be stated. ${coverage} · ${finalityLabel}.`;
  }
  if (status === 'continued') {
    return `${count} Pattern Alert${count === 1 ? '' : 's'} after Control in the measured period. ${coverage} · ${finalityLabel}.`;
  }
  return `No Pattern Alerts after Control in the measured period. ${coverage} · ${finalityLabel}.`;
}

export function recurrenceIsNotAlarmCopy(): string {
  return 'Recurrence is the Overview occurrence rate. It is not a Pattern Alert.';
}

export function scanIsNotPatternAlertCopy(): string {
  return 'Scan is governed verification of the condition after Control. It is not a Pattern Alert.';
}

export function immediateVsPatternCopy(kind: 'IMMEDIATE' | 'TEMPORAL_PATTERN' | null | undefined): string {
  if (kind === 'TEMPORAL_PATTERN') {
    return 'This alarm is a Pattern Alert (TEMPORAL_PATTERN), not an immediate incident.';
  }
  if (kind === 'IMMEDIATE') {
    return 'This alarm is an immediate incident, not a Pattern Alert.';
  }
  return 'Alarm identity is not linked.';
}
